import { useMemo } from 'react';
import { ClassifierType } from '@dealstack/shared/lib/generated';
import { useMipClassifiersList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import type { RuleCohort } from '@dealstack/frontend/shared/components/DocumentEditor/rules.types';

/**
 * The programme's active cohorts, for the conditional-content rule picker.
 *
 * A thin wrapper rather than the list hook used directly, for the same reason the data-point
 * catalogue has one: the route param belongs to this layer, so the editor can be rendered in a
 * test without a router around it.
 *
 * Archived cohorts are left out — a rule pinned to one would still evaluate, but offering it as
 * a new choice invites a condition nobody is maintaining membership for.
 *
 * The type is filtered HERE, not asked of the endpoint: `/mip-classifier` takes only
 * `isArchived`, so a `classifiedType` on the query string is dropped on the floor. It happens to
 * return cohorts only because creation hardcodes the type today, and the day that stops being
 * true an `inCohort` rule pinned to some other classifier would answer false for every investor
 * — the projection behind `data.investor.cohorts` carries INVESTOR_COHORT memberships alone —
 * and the clause would vanish from every deed with nothing said.
 */
export const useProgrammeCohorts = (): RuleCohort[] => {
	const { mipId } = useRequiredParams('mipId');
	const { data } = useMipClassifiersList({ mipId, isArchived: false });

	return useMemo(
		() =>
			(data ?? [])
				.filter((row) => row.classifiedType === ClassifierType.INVESTOR_COHORT)
				.map((row) => ({ id: row.id, name: row.name })),
		[data]
	);
};
